// ============================================================
// SectionCards.tsx
// The view/edit card engine for OBJECT case sections (Accident details,
// Injury details, …) — the generalisation of ClientInfoSections the batch-1
// mockup specifies ("How it scales: one registry per section"). Same
// anatomy, same exact classes (CARD / CARD_EDITING / BTN are imported from
// ClientInfoSections):
//   • VIEW (default) — surface cards of labelled values, hover pencil
//   • PER-CARD edit  — pencil → that card's inputs + Cancel / Save footer
//   • EDIT ALL       — every card into inputs + one Save/Cancel (header +
//                      sticky save bar)
//
// SAVE MODEL — unchanged from the old forms. Inputs write straight through
// the section's own `set` (→ updateCase → the debounced case autosave, the
// field-edit live sync + presence via each FormField's fieldKey). On top:
//   Save   → that card's validation keys must be clear → onFlush() → view
//   Cancel → restore the values captured when editing began in ONE write,
//            then broadcast the restored fieldKeys (programmatic change)
// Live documents (Yjs rich text) are never in a card's `fields`, so Cancel
// never touches them — the edit footer hint says so.
//
// Cards are RENDER FUNCTIONS (view/edit), not components, so a section can
// build its registry inside render (closures over the case id, collab user…)
// without remounting inputs on every keystroke.
// ============================================================
import React, { useState } from 'react'
import type { ValidationError } from '../../../utils/validation'
import { CARD, CARD_EDITING, BTN_SEC, BTN_PRI, SM } from '../../clientInfo/ClientInfoSections'
import { IcEdit, IcCheck } from '../../clientInfo/clientInfoRegistry'
import { SectionHead } from './sectionKit'
import CardPresence from '../../shared/CardPresence'
import { flushLiveDocSnapshots, saveDidFail, SAVE_FAILED_MSG } from '../../shared/liveDocFlush'
import { useFieldFocusRequest } from '../../../utils/fieldFocusRequest'
import { useCasevoStore } from '../../../store/useCasevoStore'
import { useLiveEditNotice } from '../../../hooks/useLiveEditNotice'
import LiveUpdateNote from '../../shared/LiveUpdateNote'
import type { CaseChangeKind } from '../../../utils/caseChangeEvents'

// (D12 live updates) The case-row data these cards edit. While a card is open,
// someone else's change to it doesn't re-read the case under the user's
// inputs: the open card says "Updated by … — Reload" and the re-read waits.
const CASE_ROW_KINDS: CaseChangeKind[] = ['case', 'clientDetails', 'parties', 'accident', 'medical', 'funding']

export interface CardCtx<T> {
  value: T
  // Show a field's validation message (only once that card's Save was tried).
  err: (key: string) => string | undefined
  // Open this card for editing (the "Add" link on an empty value) — view only.
  onAdd?: () => void
  idPrefix: string
}
export interface CardDef<T> {
  key: string
  title: string
  sub: string
  icon: React.ReactNode
  // Full-width card; 'edit' = full width only while editing (mockup Location).
  full?: boolean | 'edit'
  // Snapshot fields for Cancel (live documents deliberately excluded).
  fields: (keyof T)[]
  // Validation keys this card owns (blocks its Save; shown on Save attempt).
  errorKeys?: string[]
  hint?: string
  // Presence keys for the header "who's editing" stack. Defaults to the
  // fieldKeys of `fields`; set it when a card has fields outside its Cancel
  // snapshot (a live document's key, an address-search box).
  presenceKeys?: string[]
  headerExtra?: React.ReactNode
  view: (ctx: CardCtx<T>) => React.ReactNode
  edit: (ctx: CardCtx<T>) => React.ReactNode
}

export interface SectionCardsProps<T> {
  value: T
  // ONE store write for a multi-field change (Cancel restore).
  onChange: (changes: Partial<T>) => void
  // fieldKey (data-field / live-sync key) for a field, if it has one.
  fieldKeyOf: (field: keyof T) => string | undefined
  onBroadcast: (fieldKeys: string[]) => void
  onFlush: () => Promise<void>
  errors: ValidationError[]
  defs: CardDef<T>[]
  title: string
  sub?: React.ReactNode
  glance?: React.ReactNode
  footer?: React.ReactNode
  idPrefix: string
  // Optional strip between the glance and the cards (Client source's
  // "Not applicable" control). Hidden while Edit all is open.
  notice?: React.ReactNode
  // Hide the header "Edit all" (e.g. no editable cards are shown).
  hideEditAll?: boolean
}

export function SectionCards<T>({
  value, onChange, fieldKeyOf, onBroadcast, onFlush, errors, defs, title, sub, glance, footer, idPrefix, notice, hideEditAll,
}: SectionCardsProps<T>): React.ReactElement {
  const [editing, setEditing] = useState<Set<string>>(new Set())
  const [all, setAll] = useState(false)
  const [snapshot, setSnapshot] = useState<Partial<T>>({})
  const [attempted, setAttempted] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState<string | null>(null)
  // Card (or 'all') whose last Save failed → stays open with a message.
  const [failed, setFailed] = useState<string | null>(null)

  const errFor = (k: string): string | undefined => errors.find((e) => e.field === k)?.message
  const hasErrors = (keys: string[] | undefined): boolean => !!keys && errors.some((e) => keys.includes(e.field))
  const capture = (fields: (keyof T)[]): Partial<T> => {
    const out: Partial<T> = {}
    for (const f of fields) out[f] = value[f]
    return out
  }
  const restore = (fields: (keyof T)[]): void => {
    const changed = fields.filter((f) => f in snapshot && snapshot[f] !== value[f])
    if (changed.length === 0) return
    const changes: Partial<T> = {}
    for (const f of changed) changes[f] = snapshot[f]
    onChange(changes)
    onBroadcast(Array.from(new Set(changed.map(fieldKeyOf).filter((k): k is string => !!k))))
  }
  const open = (def: CardDef<T>): void => {
    setSnapshot((s) => ({ ...s, ...capture(def.fields) }))
    setEditing((e) => new Set(e).add(def.key))
  }
  const close = (key: string): void => {
    setFailed((f) => (f === key ? null : f))
    setEditing((e) => { const n = new Set(e); n.delete(key); return n })
    setAttempted((a) => { const n = new Set(a); n.delete(key); return n })
  }
  const save = async (def: CardDef<T>): Promise<void> => {
    if (hasErrors(def.errorKeys)) { setAttempted((a) => new Set(a).add(def.key)); return }
    setSaving(def.key)
    // Live documents' pending snapshots first, so Save persists them NOW.
    setFailed(null)
    try { await flushLiveDocSnapshots(); await onFlush() } finally { setSaving(null) }
    if (saveDidFail()) { setFailed(def.key); return }
    close(def.key)
  }
  const cancel = (def: CardDef<T>): void => { restore(def.fields); close(def.key) }

  const allFields = defs.flatMap((d) => d.fields)
  const allErrorKeys = defs.flatMap((d) => d.errorKeys ?? [])
  const editAll = (): void => { setSnapshot(capture(allFields)); setAttempted(new Set()); setAll(true) }
  const saveAll = async (): Promise<void> => {
    if (hasErrors(allErrorKeys)) { setAttempted(new Set(['all'])); return }
    setSaving('all')
    setFailed(null)
    try { await flushLiveDocSnapshots(); await onFlush() } finally { setSaving(null) }
    if (saveDidFail()) { setFailed('all'); return }
    setAll(false); setAttempted(new Set())
  }
  const cancelAll = (): void => { restore(allFields); setAll(false); setAttempted(new Set()); setFailed(null) }
  const anyOpen = editing.size > 0
  // (D12) Inputs save as you type, so nothing is thrown away: Reload closes
  // the open card(s) — keeping what's saved — and the waiting re-read lands.
  const liveCaseId = useCasevoStore((s) => s.currentCase?.id ?? null)
  const live = useLiveEditNotice(liveCaseId, CASE_ROW_KINDS, all || anyOpen, { holdCaseRefresh: true })
  const reloadLive = (): void => {
    setAll(false); setEditing(new Set()); setAttempted(new Set()); setFailed(null)
    live.clear()
  }
  const liveNote = <LiveUpdateNote notice={live.notice} dirty={false} onReload={reloadLive} className="mb-[10px]" />
  // "Take me to that field" (the case page's required-fields card): open the
  // card that owns the field, then focus it.
  useFieldFocusRequest(
    (field) => defs.find((d) => d.errorKeys?.includes(field))?.key ?? null,
    (key) => { const def = defs.find((d) => d.key === key); if (def && !all && !editing.has(key)) open(def) },
    (key) => all || editing.has(key),
    (key) => `[data-focus-card="${idPrefix}-${key}"]`,
  )

  return (
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      <SectionHead
        title={all ? `Editing ${title.charAt(0).toLowerCase()}${title.slice(1)}` : title}
        sub={sub}
        actions={all ? (
          <>
            <button type="button" className={BTN_SEC} onClick={cancelAll} disabled={saving === 'all'}>Cancel</button>
            <button type="button" className={BTN_PRI} onClick={() => { void saveAll() }} disabled={saving === 'all'}>{IcCheck(14)}{saving === 'all' ? 'Saving…' : 'Save changes'}</button>
          </>
        ) : hideEditAll ? undefined : (
          <button type="button" className={BTN_SEC} onClick={editAll} disabled={anyOpen} title={anyOpen ? 'Finish the open card first' : undefined}>{IcEdit(15)}Edit all</button>
        )}
      />
      {!all && glance}
      {!all && notice}
      {all && liveNote}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[14px] items-start">
        {defs.map((def) => {
          const isEditing = all || editing.has(def.key)
          const solo = isEditing && !all
          const show = attempted.has(def.key) || attempted.has('all')
          const ctx: CardCtx<T> = {
            value,
            err: (k) => (show ? errFor(k) : undefined),
            onAdd: !isEditing ? () => open(def) : undefined,
            idPrefix: `${idPrefix}-${def.key}`,
          }
          const full = def.full === true || (def.full === 'edit' && isEditing)
          const pKeys = new Set(def.presenceKeys ?? def.fields.map(fieldKeyOf).filter((k): k is string => !!k))
          return (
            <section key={def.key} aria-label={def.title} data-focus-card={`${idPrefix}-${def.key}`} className={`${CARD} group/card overflow-hidden flex flex-col transition-[border-color,box-shadow] ${full ? 'md:col-span-2' : ''} ${solo ? CARD_EDITING : ''}`}>
              <div className="flex items-center gap-[11px] pt-[14px] pr-4 pb-3 pl-[18px]">
                <span className="w-8 h-8 rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center shrink-0">{def.icon}</span>
                <div className="min-w-0">
                  <h3 className="m-0 text-[15px] font-semibold tracking-[-0.005em] leading-[1.2] text-casevo-forest">{def.title}</h3>
                  <div className="text-[12px] text-casevo-text-soft mt-px">{def.sub}</div>
                </div>
                <div className="ml-auto flex gap-1 items-center">
                  {/* Who's editing this card (shared presence — visible while viewing). */}
                  <CardPresence match={(k) => pKeys.has(k)} className="mr-1" />
                  {def.headerExtra}
                  {solo && <span className="text-[12.5px] text-casevo-text-soft">Editing</span>}
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => open(def)}
                      disabled={all}
                      title={`Edit ${def.title}`}
                      aria-label={`Edit ${def.title}`}
                      className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] transition-[opacity,background-color,color] focus:outline-none casevo-focus md:opacity-0 md:group-hover/card:opacity-100 md:group-focus-within/card:opacity-100"
                    >
                      {IcEdit(15)}
                    </button>
                  )}
                </div>
              </div>
              <div className="pt-[2px] px-[18px] pb-4 flex-1 min-w-0">{solo && liveNote}{isEditing ? def.edit(ctx) : def.view(ctx)}</div>
              {solo && (
                <div className="flex items-center gap-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
                  <span className="mr-auto text-[12px] text-casevo-text-soft flex items-center gap-1.5">
                    <i className="w-[7px] h-[7px] rounded-full bg-casevo-mid" aria-hidden="true" />{def.hint ?? 'Saves as you type · Cancel undoes'}
                  </span>
                  {show && hasErrors(def.errorKeys) && <span role="alert" className="text-[12px] text-casevo-danger">Fix the highlighted field to save.</span>}
                  {failed === def.key && <span role="alert" className="text-[12px] text-casevo-danger">{SAVE_FAILED_MSG}</span>}
                  <button type="button" className={`${BTN_SEC} ${SM}`} onClick={() => cancel(def)} disabled={saving === def.key}>Cancel</button>
                  <button type="button" className={`${BTN_PRI} ${SM}`} onClick={() => { void save(def) }} disabled={saving === def.key}>{IcCheck(13)}{saving === def.key ? 'Saving…' : 'Save'}</button>
                </div>
              )}
            </section>
          )
        })}
      </div>
      {all ? (
        <div role="region" aria-label="Unsaved changes" className="sticky bottom-4 z-30 mt-4 flex items-center gap-[10px] flex-wrap pl-4 pr-3 py-[10px] rounded-[14px] bg-casevo-surface border border-casevo-mid shadow-[0_12px_32px_rgba(16,42,29,0.16)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.45)]">
          <span className="w-2 h-2 rounded-full bg-casevo-mid" aria-hidden="true" />
          <span className="text-[13.5px] text-casevo-forest"><b>Editing all sections</b> <span className="text-casevo-text-soft">· saves as you type · Cancel undoes everything since Edit all</span></span>
          {attempted.has('all') && hasErrors(allErrorKeys) && <span role="alert" className="text-[12.5px] text-casevo-danger">Fix the highlighted fields to save.</span>}
          {failed === 'all' && <span role="alert" className="text-[12.5px] text-casevo-danger">{SAVE_FAILED_MSG}</span>}
          <div className="ml-auto flex gap-2">
            <button type="button" className={BTN_SEC} onClick={cancelAll} disabled={saving === 'all'}>Cancel</button>
            <button type="button" className={BTN_PRI} onClick={() => { void saveAll() }} disabled={saving === 'all'}>{IcCheck(14)}{saving === 'all' ? 'Saving…' : 'Save changes'}</button>
          </div>
        </div>
      ) : footer}
    </div>
  )
}
export default SectionCards
